import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { io } from 'socket.io-client';
import api from '../services/api';
import './SystemHubClientes.css';
import {
  FaPlus,
  FaUsers,
  FaPencilAlt,
  FaTrash,
  FaSignInAlt,
  FaEye,
  FaCopy,
  FaCheckCircle,
  FaTag,
  FaBuilding,
  FaAlignLeft,
  FaHeading,
  FaInbox,
} from 'react-icons/fa';
import SettingsModal from './SettingsModal';
import CentralConversoes from './CentralConversoes';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES
// ─────────────────────────────────────────────────────────────────────────────
const PLAN_OPTIONS = [
  { id: 'starter', nome: 'Starter' },
  { id: 'premium', nome: 'Premium' },
  { id: 'advanced', nome: 'Advanced' },
];

const FEATURE_OPTIONS = [
  { id: 'acesso_api', label: 'Acesso à API Externa' },
  { id: 'roteirizacao_automatica', label: 'Roteirização Automática' },
  { id: 'uso_ia', label: 'Integração com Inteligência Artificial' },
  { id: 'personalizacao_dashboard', label: 'Personalização do Dashboard (Whitelabel)' },
  { id: 'suporte_prioritario', label: 'Suporte Prioritário (SLA Reduzido)' },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard?.writeText(String(text));
    return true;
  } catch {
    return false;
  }
};

const formatDateBR = (dateValue) => {
  if (!dateValue) return '-';
  try {
    return new Date(dateValue).toLocaleDateString('pt-BR');
  } catch {
    return '-';
  }
};

const getLicensePercent = (used, total) => {
  const safeTotal = Number(total) > 0 ? Number(total) : 1;
  return (Number(used || 0) / safeTotal) * 100;
};

const getLicenseBarClass = (percent) => {
  if (percent > 100) return 'over';
  if (percent > 85) return 'warning';
  return '';
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTES DE APOIO
// ─────────────────────────────────────────────────────────────────────────────
function TenantIdBadge({ id, size = 'normal' }) {
  const [copied, setCopied] = useState(false);
  const small = size === 'small';

  const handleCopy = async (e) => {
    e.stopPropagation();
    const ok = await copyToClipboard(id);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <button
      type="button"
      title={`Tenant ID: ${id} — clique para copiar`}
      onClick={handleCopy}
      className={`sh-tenant-badge ${small ? 'small' : ''}`}
    >
      <FaTag size={small ? 9 : 10} />
      <span>tenant #{id}</span>
      {copied ? (
        <FaCheckCircle size={small ? 10 : 11} className="success" />
      ) : (
        <FaCopy size={small ? 10 : 11} className="copy" />
      )}
    </button>
  );
}

function TenantCreatedModal({ cliente, onEnterTenant, onPreview, onClose }) {
  const handleCopyTenant = async () => {
    const ok = await copyToClipboard(cliente.tenant_id);
    if (ok) toast.info('Tenant ID copiado!');
  };

  return (
    <div className="sh-modal-overlay sh-modal-overlay-soft">
      <div className="sh-success-modal">
        <div className="sh-success-modal__emoji">🎉</div>

        <h2 className="sh-success-modal__title">Tenant criado com sucesso!</h2>
        <p className="sh-success-modal__subtitle">
          <strong>{cliente.nome_empresa}</strong> já está ativo no sistema.
        </p>

        <div className="sh-success-modal__tenant-card">
          <p className="sh-success-modal__tenant-label">TENANT ID ATRIBUÍDO</p>

          <div className="sh-success-modal__tenant-number-row">
            <span className="sh-success-modal__tenant-number">#{cliente.tenant_id}</span>

            <button
              type="button"
              onClick={handleCopyTenant}
              className="sh-btn sh-btn-secondary sh-btn-sm"
            >
              <FaCopy size={11} />
              Copiar
            </button>
          </div>

          <p className="sh-success-modal__tenant-help">
            Este ID é usado no campo <code>tenant_id</code> em todas as tabelas de negócio
            deste cliente.
          </p>
        </div>

        <div className="sh-success-modal__actions">
          <button type="button" onClick={onEnterTenant} className="sh-btn sh-btn-primary sh-btn-lg">
            <FaSignInAlt />
            Entrar como este Tenant agora
          </button>

          <button type="button" onClick={onPreview} className="sh-btn sh-btn-secondary sh-btn-lg">
            <FaEye />
            Abrir Preview de Login
          </button>

          <button type="button" onClick={onClose} className="sh-btn sh-btn-ghost sh-btn-lg">
            Fechar e continuar no System Hub
          </button>
        </div>
      </div>
    </div>
  );
}

function DashboardCustomizationFields({ values, onChange }) {
  const { sidebarLabel, dashboardTitle, dashboardText } = values;

  return (
    <div className="sh-customization-block">
      <div className="sh-section-intro">
        <span className="sh-section-chip">📊 Dados exibidos no Dashboard</span>
        <span className="sh-section-help">
          Estes campos alimentam a tela inicial dos usuários desta empresa.
        </span>
      </div>

      <div className="sh-form-group">
        <label className="sh-label-inline">
          <FaBuilding size={12} />
          Nome de Exibição da Empresa
          <span>(aparece no “Bem-vindo” do Dashboard)</span>
        </label>
        <input
          type="text"
          value={sidebarLabel}
          onChange={(e) => onChange('sidebarLabel', e.target.value)}
          placeholder="Ex: Guincho Oliveira Transportes"
          maxLength={80}
        />
      </div>

      <div className="sh-form-group">
        <label className="sh-label-inline">
          <FaHeading size={12} />
          Título da Seção Institucional
          <span>(ex: “Sobre a Empresa”)</span>
        </label>
        <input
          type="text"
          value={dashboardTitle}
          onChange={(e) => onChange('dashboardTitle', e.target.value)}
          placeholder="Ex: Sobre a Guincho Oliveira"
          maxLength={100}
        />
      </div>

      <div className="sh-form-group">
        <label className="sh-label-inline">
          <FaAlignLeft size={12} />
          Texto Institucional
          <span>(parágrafo exibido no Dashboard)</span>
        </label>

        <textarea
          value={dashboardText}
          onChange={(e) => onChange('dashboardText', e.target.value)}
          placeholder="Descreva brevemente a empresa, serviços, diferenciais ou missão. Máximo 500 caracteres."
          maxLength={500}
          rows={4}
        />

        <span className="sh-text-counter">{dashboardText.length}/500 caracteres</span>
      </div>
    </div>
  );
}

function PlanManagementModal({ onClose, onSave }) {
  const [plans] = useState([
    { id: 'starter', nome: 'Starter', ativo: true },
    { id: 'premium', nome: 'Premium', ativo: true },
    { id: 'advanced', nome: 'Advanced', ativo: true },
  ]);

  const [permissions, setPermissions] = useState({
    starter: {
      acesso_api: false,
      roteirizacao_automatica: false,
      uso_ia: false,
      personalizacao_dashboard: false,
      suporte_prioritario: false,
    },
    premium: {
      acesso_api: true,
      roteirizacao_automatica: true,
      uso_ia: false,
      personalizacao_dashboard: true,
      suporte_prioritario: false,
    },
    advanced: {
      acesso_api: true,
      roteirizacao_automatica: true,
      uso_ia: true,
      personalizacao_dashboard: true,
      suporte_prioritario: true,
    },
  });

  const handleTogglePermission = (planId, featureId) => {
    setPermissions((prev) => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        [featureId]: !prev[planId][featureId],
      },
    }));
  };

  const handleSave = () => {
    onSave(permissions);
    toast.success('Permissões dos planos atualizadas com sucesso!');
    onClose();
  };

  return (
    <div className="sh-modal-overlay sh-modal-overlay-soft">
      <div className="sh-modal-content-large sh-plan-modal">
        <button type="button" onClick={onClose} className="sh-modal-close">
          &times;
        </button>

        <h3 className="sh-modal-title">Matriz de Permissões por Plano</h3>
        <p className="sh-modal-subtitle">
          Defina quais funcionalidades estão liberadas para cada nível de assinatura.
        </p>

        <div className="sh-plan-table-wrapper">
          <table className="sh-plan-table">
            <thead>
              <tr>
                <th className="sh-feature-col">Funcionalidade / Feature</th>
                {plans.map((plan) => (
                  <th key={plan.id} className="sh-plan-col">
                    <span className={`sh-plan-badge sh-plan-${plan.id}`}>{plan.nome}</span>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {FEATURE_OPTIONS.map((feature) => (
                <tr key={feature.id}>
                  <td className="sh-feature-name">
                    <FaTag className="sh-feature-icon" />
                    {feature.label}
                  </td>

                  {plans.map((plan) => (
                    <td key={plan.id} className="sh-plan-cell">
                      <label className="sh-toggle-switch">
                        <input
                          type="checkbox"
                          checked={permissions[plan.id][feature.id]}
                          onChange={() => handleTogglePermission(plan.id, feature.id)}
                        />
                        <span className="sh-toggle-slider" />
                      </label>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="sh-modal-footer-simple">
          <button type="button" onClick={onClose} className="sh-btn sh-btn-ghost">
            Cancelar
          </button>

          <button type="button" onClick={handleSave} className="sh-btn sh-btn-primary">
            <FaCheckCircle />
            Salvar Matriz de Permissões
          </button>
        </div>
      </div>
    </div>
  );
}

const CreateClientModal = ({ onClose, onSave }) => {
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [documentoEmpresa, setDocumentoEmpresa] = useState('');
  const [maxLicencas, setMaxLicencas] = useState(5);
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [nomeContato, setNomeContato] = useState('');
  const [emailContato, setEmailContato] = useState('');
  const [telefoneContato, setTelefoneContato] = useState('');

  const [customization, setCustomization] = useState({
    sidebarLabel: '',
    dashboardTitle: '',
    dashboardText: '',
  });

  const handleCustomizationChange = useCallback((field, value) => {
    setCustomization((prev) => ({ ...prev, [field]: value }));
  }, []);

  useEffect(() => {
    if (!customization.sidebarLabel) {
      setCustomization((prev) => ({ ...prev, sidebarLabel: nomeEmpresa }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomeEmpresa]);

  const handleSubmit = (e) => {
    e.preventDefault();

    onSave({
      nome_empresa: nomeEmpresa,
      max_licencas: maxLicencas,
      plano_id: selectedPlan,
      contato_principal: {
        nome: nomeContato,
        email: emailContato,
        telefone: telefoneContato,
        documento: documentoEmpresa,
      },
      customization,
    });
  };

  return (
    <div className="sh-modal-overlay sh-modal-overlay-soft">
      <div className="sh-modal-create-content sh-modal-create-content--wide">
        <button type="button" onClick={onClose} className="sh-modal-close">
          &times;
        </button>

        <h3 className="sh-modal-title">Novo Cliente / Tenant</h3>

        <div className="sh-info-banner">
          <FaTag className="sh-info-banner__icon" />
          <div>
            Ao criar o cliente, o sistema atribuirá automaticamente um <strong>Tenant ID</strong>{' '}
            único. Os dados de personalização abaixo serão exibidos no Dashboard dos usuários desta
            empresa.
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="sh-form-grid-two">
            <div className="sh-form-section-card">
              <h4 className="sh-form-section-title">Dados da Empresa</h4>

              <div className="sh-form-group">
                <label>Nome da Empresa</label>
                <input
                  type="text"
                  value={nomeEmpresa}
                  onChange={(e) => setNomeEmpresa(e.target.value)}
                  required
                  placeholder="Ex: Guincho Oliveira"
                />
              </div>

              <div className="sh-form-group">
                <label>CNPJ / CPF</label>
                <input
                  type="text"
                  value={documentoEmpresa}
                  onChange={(e) => setDocumentoEmpresa(e.target.value)}
                  required
                  placeholder="Apenas números"
                />
              </div>

              <div className="sh-form-group">
                <label>Número de Licenças</label>
                <input
                  type="number"
                  min="1"
                  value={maxLicencas}
                  onChange={(e) => setMaxLicencas(parseInt(e.target.value, 10) || 1)}
                  required
                />
              </div>

              <div className="sh-form-group">
                <label>Plano de Assinatura</label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  required
                >
                  {PLAN_OPTIONS.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sh-form-section-card">
              <h4 className="sh-form-section-title">Contato Principal (Cobrança)</h4>

              <div className="sh-form-group">
                <label>Nome do Responsável</label>
                <input
                  type="text"
                  value={nomeContato}
                  onChange={(e) => setNomeContato(e.target.value)}
                  required
                  placeholder="Ex: João Silva"
                />
              </div>

              <div className="sh-form-group">
                <label>E-mail</label>
                <input
                  type="email"
                  value={emailContato}
                  onChange={(e) => setEmailContato(e.target.value)}
                  required
                  placeholder="financeiro@empresa.com"
                />
              </div>

              <div className="sh-form-group">
                <label>Telefone (WhatsApp)</label>
                <input
                  type="text"
                  value={telefoneContato}
                  onChange={(e) => setTelefoneContato(e.target.value)}
                  required
                  placeholder="DDD + Número"
                />
              </div>
            </div>
          </div>

          <DashboardCustomizationFields
            values={customization}
            onChange={handleCustomizationChange}
          />

          <div className="sh-modal-footer-simple">
            <button type="submit" className="sh-btn sh-btn-primary sh-btn-full">
              Criar Cliente e Vincular Tenant ID
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TABS: Clientes cadastrados  ×  Central de Conversões
// ─────────────────────────────────────────────────────────────────────────────
function SystemHubTabs({ view, onChange, pendentesCount }) {
  const tabStyle = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 18px',
    borderRadius: 10,
    border: '1px solid ' + (active ? 'transparent' : '#e5e7eb'),
    background: active ? '#101c5d' : '#fff',
    color: active ? '#fff' : '#4b5563',
    fontWeight: 700,
    fontSize: '0.88rem',
    cursor: 'pointer',
  });

  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
      <button type="button" style={tabStyle(view === 'clientes')} onClick={() => onChange('clientes')}>
        <FaUsers /> Clientes cadastrados
      </button>
      <button type="button" style={tabStyle(view === 'conversoes')} onClick={() => onChange('conversoes')}>
        <FaInbox /> Central de Conversões
        {pendentesCount > 0 && (
          <span style={{
            background: view === 'conversoes' ? '#ff8c00' : '#fdecef',
            color: view === 'conversoes' ? '#fff' : '#b91c1c',
            borderRadius: 999, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 800,
          }}>
            {pendentesCount}
          </span>
        )}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function SystemHubClientes() {
  const navigate = useNavigate();

  const [view, setView] = useState('clientes'); // 'clientes' | 'conversoes'
  const [pendentesCount, setPendentesCount] = useState(0);
  const [socket, setSocket] = useState(null);

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [createdClient, setCreatedClient] = useState(null);
  const [switchingId, setSwitchingId] = useState(null);

  // ── FETCH ────────────────────────────────────────────────────────────────
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/system-hub/clientes');
      setClients(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Falha ao carregar a lista de clientes.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Contagem leve só para o badge da aba (a Central de Conversões faz seu
  // próprio fetch paginado quando ativa). A rota agora responde
  // { items, total, counts } — com limit:1 não trazemos linha nenhuma, só o total.
  const fetchPendentesCount = useCallback(async () => {
    try {
      const { data } = await api.get('/api/vendas/conversoes', {
        params: { status: 'aguardando_cs', limit: 1 },
      });
      setPendentesCount(Number(data?.total ?? data?.counts?.pendentes ?? 0));
    } catch {
      // silencioso — é só um badge informativo
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchPendentesCount();
  }, [fetchClients, fetchPendentesCount]);

  // ── SOCKET.IO ──────────────────────────────────────────────────────────────
  // Conexão própria do painel (mesmo padrão do CentralOperacoes). Manda o token
  // no handshake para o autoRegisterByToken do backend registrar o admin_geral.
  // Os eventos conversoes:* saem por io.emit (broadcast), então basta estar
  // conectado. Também mantém o badge da aba vivo em tempo real.
  //
  // NOVO: 'tenant:contrato_atualizado' — disparado pelo webhook da ClickSign
  // quando um contrato é assinado DEPOIS que o tenant já existe (o CS cadastrou
  // o cliente antes do cliente final assinar). Atualiza a lista e, se o
  // SettingsModal do mesmo tenant estiver aberto, atualiza ele também na hora.
  useEffect(() => {
    const base = api.defaults.baseURL || window.location.origin;
    const token = localStorage.getItem('token');
    const s = io(base, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      auth: token ? { token } : undefined,
    });
    setSocket(s);

    const bump = () => fetchPendentesCount();

    const onContratoAtualizado = ({ clienteId, contrato_url, contrato_assinado_em }) => {
      if (!clienteId) return;

      setClients((prev) => prev.map((c) => (
        c.id === clienteId
          ? { ...c, contrato_url, contrato_origem: 'clicksign', contrato_assinado_em: contrato_assinado_em || new Date().toISOString() }
          : c
      )));

      setSelectedClient((prev) => (
        prev && prev.id === clienteId
          ? { ...prev, contrato_url, contrato_origem: 'clicksign', contrato_assinado_em: contrato_assinado_em || new Date().toISOString() }
          : prev
      ));

      toast.success('Contrato assinado recebido automaticamente! 📄');
    };

    s.on('conversoes:novo_ticket', bump);
    s.on('conversoes:atualizado', bump);
    s.on('tenant:contrato_atualizado', onContratoAtualizado);

    return () => {
      s.off('conversoes:novo_ticket', bump);
      s.off('conversoes:atualizado', bump);
      s.off('tenant:contrato_atualizado', onContratoAtualizado);
      s.disconnect();
    };
  }, [fetchPendentesCount]);

  // ── CRIAR CLIENTE ───────────────────────────────────────────────────────
  const handleCreateClient = async (clientData) => {
    try {
      const { data: responseEmpresa } = await api.post('/api/system-hub/clientes', {
        nome_empresa: clientData.nome_empresa,
        max_licencas: clientData.max_licencas,
        plano_id: clientData.plano_id,
      });

      const novoClienteId = responseEmpresa.id;
      const tenantId = responseEmpresa.tenant_id;

      await api.post(`/api/system-hub/clientes/${novoClienteId}/contatos`, {
        nome: clientData.contato_principal.nome,
        email: clientData.contato_principal.email,
        telefone: clientData.contato_principal.telefone,
        documento: clientData.contato_principal.documento,
        cargo_tag: 'Admin',
        is_principal: true,
        is_billing: true,
      });

      const { sidebarLabel, dashboardTitle, dashboardText } = clientData.customization || {};
      const hasCustomization = sidebarLabel || dashboardTitle || dashboardText;

      if (hasCustomization) {
        const formData = new FormData();

        if (sidebarLabel) formData.append('sidebarLabel', sidebarLabel);
        if (dashboardTitle) formData.append('dashboardTitle', dashboardTitle);
        if (dashboardText) formData.append('dashboardText', dashboardText);

        await api.post(`/api/system-hub/clientes/${novoClienteId}/customize`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      await fetchClients();
      setIsCreateModalOpen(false);

      setCreatedClient({
        id: novoClienteId,
        tenant_id: tenantId,
        nome_empresa: clientData.nome_empresa,
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível criar o cliente.');
    }
  };

  // ── ENTRAR COMO TENANT ──────────────────────────────────────────────────
  const handleEnterTenant = async (clientId) => {
    setSwitchingId(clientId);

    try {
      const { data } = await api.post(`/api/system-hub/clientes/${clientId}/switch-access`);
      const newToken = data?.token;

      if (!newToken) {
        throw new Error('Token não retornado.');
      }

      localStorage.setItem('token', newToken);
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;

      toast.success(`Você está agora operando como: ${data.tenant.nome_empresa}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao entrar no tenant.');
    } finally {
      setSwitchingId(null);
    }
  };

  // ── PREVIEW DE LOGIN ────────────────────────────────────────────────────
  const handlePreviewTenant = async (clientId) => {
    try {
      const { data } = await api.post(`/api/system-hub/clientes/${clientId}/generate-preview`);

      if (!data?.preview_url) {
        throw new Error('URL de preview não retornada.');
      }

      window.open(data.preview_url, '_blank', 'noopener,noreferrer');
      toast.info(`Preview aberto para: ${data.empresa} (válido por ${data.expires_in})`);
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.error?.includes('slug')) {
        toast.warning('Configure um slug para este tenant antes de gerar o preview.');
      } else {
        toast.error(err.response?.data?.error || 'Falha ao gerar preview.');
      }
    }
  };

  // ── DELETAR ─────────────────────────────────────────────────────────────
  const handleDeleteClient = async (client) => {
    const confirmDelete = window.confirm(
      `ATENÇÃO: Excluir "${client.nome_empresa}" (tenant #${client.id})?\n\nSe houver dados vinculados, a exclusão será bloqueada por segurança.`
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/api/system-hub/clientes/${client.id}`);
      toast.success('Cliente removido com sucesso.');
      await fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir cliente.');
    }
  };

  const handleOpenSettings = (client) => {
    setSelectedClient(client);
    setIsSettingsModalOpen(true);
  };

  const handleCloseAndRefresh = async () => {
    setIsSettingsModalOpen(false);
    setSelectedClient(null);
    await fetchClients();
  };

  // Chamado pela Central de Conversões quando o CS cria um tenant novo:
  // atualiza a lista de clientes e o badge de pendências de uma vez.
  const handleTenantCreatedFromConversao = async () => {
    await fetchClients();
    await fetchPendentesCount();
  };

  if (loading && view === 'clientes') {
    return <div className="sh-loading-container">Carregando gestão de clientes...</div>;
  }

  return (
    <div className="sh-clientes-wrapper system-hub-container">
      {/* HEADER */}
      <div className="sh-clientes-header hub-header">
        <div className="sh-page-heading">
          <h1>
            <FaUsers />
            Gestão de Clientes
          </h1>
          <p>Painel administrativo para tenants, licenças, personalização e operação segura.</p>
        </div>

        {view === 'clientes' && (
          <div className="sh-header-actions">
            <button
              type="button"
              onClick={() => setIsPlanModalOpen(true)}
              className="sh-btn sh-btn-secondary"
            >
              <FaAlignLeft />
              Gerenciar Planos
            </button>

            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="sh-btn sh-btn-success"
            >
              <FaPlus />
              Adicionar Novo Cliente
            </button>
          </div>
        )}
      </div>

      <SystemHubTabs view={view} onChange={setView} pendentesCount={pendentesCount} />

      {view === 'conversoes' ? (
        <CentralConversoes socket={socket} onTenantCreated={handleTenantCreatedFromConversao} />
      ) : (
        <>
          {/* CARD LISTA */}
          <div className="sh-card client-list-card">
            <div className="sh-card-topbar">
              <div>
                <h2>Clientes cadastrados</h2>
                <span>{clients.length} registro(s) encontrado(s)</span>
              </div>
            </div>

            <div className="sh-table-wrapper">
              <table className="sh-clientes-table">
                <thead>
                  <tr>
                    <th className="col-empresa">Empresa</th>
                    <th className="col-tenant">Tenant ID</th>
                    <th className="col-status">Status</th>
                    <th className="col-licencas">Licenças</th>
                    <th className="col-data">Cadastro</th>
                    <th className="col-acoes">Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {clients.map((client) => {
                    const tenantDisplayId = client.tenant_id ?? client.id;
                    const pct = getLicensePercent(client.licencas_em_uso, client.max_licencas);
                    const barClass = getLicenseBarClass(pct);
                    const isSwitching = switchingId === client.id;

                    return (
                      <tr key={client.id}>
                        <td className="col-empresa">
                          <div className="sh-company-cell">
                            <strong>{client.nome_empresa}</strong>
                            {client.slug && <span className="sh-company-slug">/{client.slug}</span>}
                          </div>
                        </td>

                        <td className="col-tenant">
                          <TenantIdBadge id={tenantDisplayId} />
                        </td>

                        <td className="col-status">
                          <span className={`sh-badge status-${client.status}`}>
                            {client.status === 'ativo' ? 'Ativo' : 'Suspenso'}
                          </span>
                        </td>

                        <td className="col-licencas">
                          <div className="sh-license-wrapper">
                            <div className="sh-license-text">
                              <strong>{client.licencas_em_uso}</strong> / {client.max_licencas}
                            </div>
                            <div className="sh-license-track">
                              <div
                                className={`sh-license-fill ${barClass}`}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                          </div>
                        </td>

                        <td className="col-data">{formatDateBR(client.data_criacao)}</td>

                        <td className="col-acoes">
                          <div className="sh-actions-group">
                            <button
                              type="button"
                              title={`Operar o sistema como ${client.nome_empresa}`}
                              onClick={() => handleEnterTenant(client.id)}
                              disabled={isSwitching || client.status !== 'ativo'}
                              className={`sh-action-btn sh-action-btn-enter ${
                                isSwitching || client.status !== 'ativo' ? 'is-disabled' : ''
                              }`}
                            >
                              <FaSignInAlt size={11} />
                              {isSwitching ? 'Entrando...' : 'Entrar'}
                            </button>

                            <button
                              type="button"
                              title="Visualizar tela de login deste tenant"
                              onClick={() => handlePreviewTenant(client.id)}
                              className="sh-action-btn sh-action-btn-preview"
                            >
                              <FaEye size={11} />
                              Preview
                            </button>

                            <button
                              type="button"
                              title="Gerenciar configurações do cliente"
                              onClick={() => handleOpenSettings(client)}
                              className="sh-action-btn sh-action-btn-config"
                            >
                              <FaPencilAlt size={11} />
                              Config
                            </button>

                            <button
                              type="button"
                              title="Excluir cliente"
                              onClick={() => handleDeleteClient(client)}
                              className="sh-action-btn sh-action-btn-delete icon-only"
                            >
                              <FaTrash size={11} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {clients.length === 0 && (
              <div className="sh-empty-state">Nenhum cliente cadastrado no sistema ainda.</div>
            )}
          </div>
        </>
      )}

      {/* MODAIS */}
      {isPlanModalOpen && (
        <PlanManagementModal
          onClose={() => setIsPlanModalOpen(false)}
          onSave={(permissionsData) => {
            console.log('Permissões para salvar no DB:', permissionsData);
          }}
        />
      )}

      {isCreateModalOpen && (
        <CreateClientModal
          onClose={() => setIsCreateModalOpen(false)}
          onSave={handleCreateClient}
        />
      )}

      {createdClient && (
        <TenantCreatedModal
          cliente={createdClient}
          onEnterTenant={() => {
            setCreatedClient(null);
            handleEnterTenant(createdClient.id);
          }}
          onPreview={() => {
            handlePreviewTenant(createdClient.id);
          }}
          onClose={() => setCreatedClient(null)}
        />
      )}

      {isSettingsModalOpen && selectedClient && (
        <SettingsModal
          client={selectedClient}
          onClose={() => setIsSettingsModalOpen(false)}
          onSaveSuccess={handleCloseAndRefresh}
        />
      )}
    </div>
  );
}